import { EVENT_KEYS, type EventKey } from './keys'
import type { DataCollector, Handler } from './types'

export { KEYS } from './keys'

export class Arcus {
  private activeCollectors = new Map<EventKey, DataCollector>()

  /**
   * Log an event with optional data.
   * If no data provided and key has collector, data is auto-collected.
   */
  log(key: EventKey, additionalData?: any): void {
    const config = EVENT_KEYS[key]
    if (!config?.handlers || config.handlers.length === 0) return

    const collectedData = config?.collector ? config.collector().collect() : {}
    const data = additionalData
      ? { ...collectedData, ...additionalData }
      : collectedData

    this.dispatch(key, config.handlers, data)
  }

  /**
   * Start a measurement for the given key.
   * Only works for keys with collectors that support start/end.
   */
  start(key: EventKey, initialData?: any): void {
    const config = EVENT_KEYS[key]
    if (!config?.collector) {
      console.warn(`No collector configured for key: ${key}`)
      return
    }

    const collector = config.collector()
    if ('start' in collector && typeof collector.start === 'function') {
      collector.start(initialData)
      this.activeCollectors.set(key, collector)
    } else {
      console.warn(`Collector for ${key} does not support start()`)
    }
  }

  /**
   * End a measurement and log the collected data.
   */
  end(key: EventKey, additionalData?: any): void {
    const collector = this.activeCollectors.get(key)
    if (!collector) {
      console.warn(`No active measurement for key: ${key}`)
      return
    }

    const collectedData = collector.collect()
    this.activeCollectors.delete(key)

    const data = additionalData
      ? { ...collectedData, ...additionalData }
      : collectedData

    this.log(key, data)
  }

  private dispatch(key: EventKey, eventHandlers: Handler[], data: any): void {
    Promise.allSettled(
      eventHandlers.map((handler) =>
        Promise.resolve(handler.handle(key, data)),
      ),
    ).catch(() => {
      // Silently fail - logging should never break the app
    })
  }
}

export const arcus = new Arcus()
